import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import {
  NavSidebar,
  SIDEBAR_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_LEFT_MARGIN,
} from './NavSidebar';
import { NavSidebarContext } from '../contexts/navSidebarContext';
import BodyWrapper from './BodyWrapper';
import Header from './Header';
import { usePageDataPrefetcher } from 'hooks/usePageDataPrefetcher';
import GuidingChecklistFloatingWidget from 'modules/GuidingChecklist/components/GuidingChecklistFloatingWidget';
import WhatsNewDrawer from 'modules/WhatsNew/components/WhatsNewDrawer';
import { useDecimalConventionStore } from 'stores/useDecimalConventionStore';
import { useOrganization } from '@clerk/clerk-react';
import { OrganizationType } from 'modules/Common/utils/Utility';
import { useRegimeProfile } from 'modules/Common/importWizard/hooks/useRegimeProfile';
import { useIdlePageChunkPreload } from '../hooks/useIdlePageChunkPreload';
import { preloadPageChunk } from '../config/routeChunks';

// Routes that host the full-bleed ReactFlow canvas. On these routes the
// global Header is replaced by DiagramEditor's own ribbon (which embeds the
// OrganizationSwitcher + account menu inline), so it would be suppressed
// here and the matching pt-[41px] spacer dropped to give the canvas the
// full viewport height. (The Header is currently commented out for every
// route — identity lives in the NavSidebar footer — so this flag is
// forward-looking chrome-suppression state kept correct for if/when the
// Header is restored.)
//
// Matched as a PREFIX (exact:false) so sub-routes of the editor — e.g.
// `/diagrams/:id/operator-reports/:reportId` — are also treated as part
// of the diagram subtree and never re-show the app header on top of the
// editor chrome.
const DIAGRAM_EDITOR_ROUTE_PATTERNS = ['/new-diagram', '/diagrams/:id'];

const COLLAPSE_KEY = 'isSidebarCollapsed';

const readCollapsePreference = () => {
  const saved = localStorage.getItem(COLLAPSE_KEY);
  return saved === null ? false : JSON.parse(saved);
};

export const AppLayout = ({ children }) => {
  const location = useLocation();
  const isDiagramEditorRoute = DIAGRAM_EDITOR_ROUTE_PATTERNS.some((pattern) =>
    matchPath(location.pathname, { path: pattern, exact: false }),
  );
  // Initialize the page data prefetcher to cache starter page data
  // This runs in the background and keeps data fresh
  // Suspended on /trial-expired: that page now renders inside this layout so the
  // user can reach the workspace switcher, but prefetching an expired org's data
  // is a guaranteed 403 every 2 minutes.
  const { prefetchRoute } = usePageDataPrefetcher({
    enabled: location.pathname !== '/trial-expired',
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Not used anymore but keeping for compatibility
  const [isSidebarHidden, setIsSidebarHidden] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [preferCollapsed, setPreferCollapsed] = useState(readCollapsePreference);
  const [collapseRequests, setCollapseRequests] = useState(0);
  const [openedThisVisit, setOpenedThisVisit] = useState(false);
  const collapseRequested = collapseRequests > 0;
  const isSidebarCollapsed = !openedThisVisit && (collapseRequested || preferCollapsed);

  const toggleCollapsed = useCallback(() => {
    if (collapseRequested) {
      setOpenedThisVisit((open) => !open);
      return;
    }
    const next = !isSidebarCollapsed;
    localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next));
    setPreferCollapsed(next);
  }, [collapseRequested, isSidebarCollapsed]);
  const requestCollapse = useCallback(() => setCollapseRequests((n) => n + 1), []);
  const releaseCollapse = useCallback(() => setCollapseRequests((n) => Math.max(0, n - 1)), []);
  useEffect(() => {
    if (!collapseRequested) setOpenedThisVisit(false);
  }, [collapseRequested]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('sidebarLayoutChange', {
        detail: { collapsed: isSidebarCollapsed, pinned: !isSidebarCollapsed },
      }),
    );
  }, [isSidebarCollapsed]);

  const navSidebar = useMemo(
    () => ({
      isCollapsed: isSidebarCollapsed,
      toggleCollapsed,
      requestCollapse,
      releaseCollapse,
    }),
    [isSidebarCollapsed, toggleCollapsed, requestCollapse, releaseCollapse],
  );

  // Subscribe to the user's decimal convention so the route content
  // re-renders when it changes. `formatNumber`/`formatDecimal`/`formatCurrency`
  // read the store at call time without subscribing, so without this any
  // already-mounted screen would keep its old formatting until something
  // else triggered a re-render. The key on the content wrapper below also
  // forces memoized descendants to pick up the change. Header is rendered
  // outside the keyed area so the Clerk Preferences modal stays open while
  // the user toggles the setting.
  const decimalConvention = useDecimalConventionStore((s) => s.convention);

  // The regime's currency, resolved once for the whole session and published
  // to `useRegimeCurrencyStore` by this hook, so that every `formatCurrency`
  // call in the app — including the ones inside Excel/PDF generators and chart
  // tick callbacks, which cannot call a hook — reads sterling for a UK CBAM
  // declarant and euros for an EU one. Mounted HERE because AppLayout is the
  // persistent shell (it survives navigation), so the answer is in before the
  // first money cell paints rather than arriving per-page.
  //
  // Declarant orgs only: an operator, a consultant or a lite supplier has no
  // `declarant.regulation` to derive a regime from, so they never fire the
  // request and the store stays empty — which `getRegimeCurrency()` reads as
  // euros, exactly what they rendered before. Same gate NavSidebar uses for
  // `useFetchDeclarant` (`NavSidebar.jsx:488-490`).
  const { organization } = useOrganization();
  useRegimeProfile({
    enabled: organization?.publicMetadata?.type === OrganizationType.Declarant,
  });

  // Warm the pages this org type opens most, one chunk per idle slot, so the
  // first visit to each does not wait on a download. Hovering a sidebar link
  // fetches that page's chunk alongside its data.
  // See docs/features/react-lazy-loading/phase-7-navigation-speed.md.
  useIdlePageChunkPreload(organization?.publicMetadata?.type, {
    enabled: location.pathname !== '/trial-expired',
  });
  const handlePrefetchRoute = useCallback(
    (url) => {
      preloadPageChunk(url);
      return prefetchRoute(url);
    },
    [prefetchRoute],
  );

  // Responsive screen width tracking
  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Set sidebar hidden on mobile screens
  useEffect(() => {
    if (screenWidth <= 1024) {
      setIsSidebarHidden(true);
    } else {
      setIsSidebarHidden(false);
    }
  }, [screenWidth]);

  // Dynamic margin based on sidebar state
  const getContentMarginLeft = () => {
    if (screenWidth <= 1024 && isSidebarHidden) {
      return '0px'; // No margin when sidebar is hidden on mobile
    }

    // Add the sidebar's left-margin to the content offset so the page body
    // starts where the floating sidebar's right edge ends, not under it.
    if (!isSidebarCollapsed) {
      return `${SIDEBAR_WIDTH + SIDEBAR_LEFT_MARGIN}px`;
    }
    return `${SIDEBAR_COLLAPSED_WIDTH + SIDEBAR_LEFT_MARGIN}px`;
  };

  // Publish the live sidebar offset (identical to the content column's
  // marginLeft: 264px pinned / 72px collapsed / 0px hidden) as the CSS var
  // --cg-sidebar-w on the documentElement, mirroring the --cg-header-h
  // idiom in Header.jsx. DiagramEditor's ribbon title bar is position:fixed and
  // reads this so its left edge starts at the sidebar's right edge instead
  // of painting over the NavSidebar column. Nothing outside DiagramEditor reads
  // the var, and it's removed on unmount, so non-diagram pages (and the
  // `var(--cg-sidebar-w, 0px)` fallback) are unaffected. useLayoutEffect
  // (not Header's useEffect) sets it before first paint so the ribbon's
  // `left` transition doesn't animate a one-time slide from 0 on mount.
  const contentMarginLeft = getContentMarginLeft();
  useLayoutEffect(() => {
    document.documentElement.style.setProperty('--cg-sidebar-w', contentMarginLeft);
    return () => {
      document.documentElement.style.removeProperty('--cg-sidebar-w');
    };
  }, [contentMarginLeft]);

  return (
    <NavSidebarContext.Provider value={navSidebar}>
      {/* Header is intentionally suppressed for this layout — the brand
          logo, organization switcher, and user account self-management
          all live in the NavSidebar footer now. Keeping the import in
          place so it can be restored quickly if needed. */}
      {/* {!isDiagramEditorRoute && (
        <Header
          isSidebarHidden={isSidebarHidden}
          onSetSideBarHidden={(val) => setIsSidebarHidden(val)}
          onSetSideBarOpen={(val) => setIsSidebarOpen(val)}
        />
      )} */}
      <BodyWrapper>
        {/* No fixed Header → no 41px spacer. Only the optional background-
            jobs banner contributes to top padding now. */}
        <div
          className="flex"
          style={{
            paddingTop: 'var(--cg-jobs-banner-h, 0px)',
          }}
        >
          <NavSidebar
            isSidebarOpen={isSidebarOpen}
            isSidebarHidden={isSidebarHidden}
            onSetSideBarOpen={(val) => setIsSidebarOpen(val)}
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={toggleCollapsed}
            onPrefetchRoute={handlePrefetchRoute}
          />
          <div className="flex flex-col flex-1 h-full">
            <main className="content">
              <section className="sm:flex-row flex flex-col flex-1">
                <div
                  key={decimalConvention || 'american'}
                  className="content-box min-h-[90vh]"
                  style={{
                    flexGrow: 2,
                    flexBasis: '0%',
                    overflow: 'visible',
                    position: 'relative',
                    marginLeft: getContentMarginLeft(),
                    transition: 'margin-left 0.3s cubic-bezier(0.22, 0.61, 0.36, 1)',
                    width: `calc(100vw - ${getContentMarginLeft()})`,
                    maxWidth: `calc(100vw - ${getContentMarginLeft()})`,
                  }}
                >
                  {children}
                </div>
              </section>
            </main>
          </div>
        </div>
      </BodyWrapper>
      <GuidingChecklistFloatingWidget />
      {/* Mounted HERE, not beside <CgToaster/> in _routes.jsx. ContentWrapper
          (_app.jsx:206-212) wraps children in AppLayout only for non-outside
          routes, and AppLayout's children are <Routes/> itself — so anything
          mounted inside _routes.jsx also renders for a signed-in user on
          /onboarding/wizard and /pricing. This position is the one that is
          skipped on every outside route. */}
      <WhatsNewDrawer />
    </NavSidebarContext.Provider>
  );
};
